import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadAnalysisPdf } from "@/lib/pdf";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ConflictConfirmDialog from "@/components/ConflictConfirmDialog";
import QuestionnaireDeclarations from "@/components/QuestionnaireDeclarations";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ChevronLeft, Download, Calendar, CheckCircle2, Home, Scissors,
  AlertCircle, Sparkles, Eye, Loader2, Receipt, ShoppingBag, Lock, Crown,
  MessageCircle, Briefcase, XCircle, RefreshCw, GitCompare, AlertTriangle,
  Send, Link as LinkIcon, Mail, Check, FileText, Share2, Info, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const formatEuro = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(value));
};

export default function AnalysisDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  // ---- Polling pipeline async (mag 2026) -----------------------------------
  // L'analisi può arrivare in 3 stati: "processing" | "completed" | "failed".
  // Se processing → polling leggero ogni 3s su /analyses/{id}/status.
  // Se completed → carichiamo il full report una sola volta.
  // Se failed → schermo di errore dedicato con CTA "Riprova".
  const [pipelineStatus, setPipelineStatus] = useState(null); // {status, progress_stage, error_code, error_message}
  const pollingRef = useRef(null);
  const waitRequested = searchParams.get("wait") === "1";

  // Dialog "Invia analisi" (apr 2026): sostituisce i 3 bottoni separati
  // (Invia WhatsApp / Scarica PDF / Anteprima portale) con un unico flusso
  // centralizzato basato sul link del Portale Cliente.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [sold, setSold] = useState(new Set());
  const [locked, setLocked] = useState(false);

  const isFree = user?.plan === "free";
  const canDownloadPdf = !isFree;

  // Carica il full report + client + sales. Usato sia al primo mount (se
  // l'analisi è già completed) sia quando il polling rileva completed.
  const loadFullAnalysis = async () => {
    try {
      const res = await api.get(`/analyses/${id}`);
      setAnalysis(res.data);
      // client e sales sono best-effort: se falliscono il report resta
      // comunque visibile (no reset dello state globale, no toast rumoroso).
      try {
        const c = await api.get(`/clients/${res.data.client_id}`);
        setClient(c.data);
      } catch { /* non critico */ }
      try {
        const s = await api.get(`/analyses/${id}/sales`);
        setSold(new Set(s.data.sold || []));
      } catch { /* non critico */ }
      return res.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === "object" && detail?.error === "analysis_locked") {
        setLocked(true);
      } else if (err.response?.status === 404) {
        toast.error("Analisi non trovata");
        nav("/");
      } else {
        toast.error("Errore nel caricamento dell'analisi");
      }
      return null;
    }
  };

  // Mount: leggiamo prima lo STATUS (leggero, niente foto). Se processing
  // partiamo col polling. Se completed/failed carichiamo il report.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const st = await api.get(`/analyses/${id}/status`);
        if (cancelled) return;
        setPipelineStatus(st.data);
        if (st.data.status === "completed") {
          await loadFullAnalysis();
        } else if (st.data.status === "failed") {
          // Nulla da caricare oltre lo status.
        } else {
          // processing → il polling sotto farà il resto
        }
      } catch (err) {
        if (err.response?.status === 404) {
          toast.error("Analisi non trovata");
          nav("/");
        } else {
          // Errore transiente — mostriamo loader, il polling riproverà.
          setPipelineStatus({ status: "processing", progress_stage: "", error_code: "", error_message: "" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Polling ogni 3s finché processing. Max 3 minuti (60 tentativi) per sicurezza.
  useEffect(() => {
    if (!pipelineStatus) return;
    if (pipelineStatus.status !== "processing") return;

    let tries = 0;
    const MAX_TRIES = 60; // 3 minuti @ 3s
    pollingRef.current = setInterval(async () => {
      tries += 1;
      try {
        const st = await api.get(`/analyses/${id}/status`);
        if (st.data.status !== "processing") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPipelineStatus(st.data);
          if (st.data.status === "completed") {
            await loadFullAnalysis();
            // Pulisci ?wait=1 dall'URL (UX)
            if (waitRequested) {
              setSearchParams({}, { replace: true });
            }
            toast.success("Analisi pronta");
          }
          // failed → la UI dedicata è già pronta
          return;
        }
        // ancora processing → aggiorniamo solo il progress_stage
        setPipelineStatus(st.data);
      } catch {
        // errore di rete/server — NON blocchiamo, riproveremo al prossimo tick
      }
      if (tries >= MAX_TRIES) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        // Non segnaliamo failed: il backend potrebbe ancora finire. Mostriamo
        // un messaggio gentile e lasciamo all'utente il controllo.
        toast.info("L'analisi sta impiegando più tempo del previsto. Ricarica la pagina tra qualche minuto.");
      }
    }, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineStatus?.status, id]);

  const toggleSold = async (productName) => {
    const isSold = sold.has(productName);
    // Optimistic update
    setSold(prev => {
      const next = new Set(prev);
      if (isSold) next.delete(productName); else next.add(productName);
      return next;
    });
    try {
      if (isSold) {
        await api.delete(`/sales`, { params: { analysis_id: id, product_name: productName } });
      } else {
        await api.post(`/sales`, {
          analysis_id: id,
          client_id: analysis.client_id,
          product_name: productName,
        });
      }
    } catch (err) {
      // Revert on failure
      setSold(prev => {
        const next = new Set(prev);
        if (isSold) next.add(productName); else next.delete(productName);
        return next;
      });
      toast.error("Errore nel salvataggio");
    }
  };

  const downloadPdf = () => {
    if (downloading) return;
    setDownloading(true);
    const ok = downloadAnalysisPdf(id);
    if (ok) {
      toast.success("PDF scaricato");
    } else {
      toast.error("Errore nel download del PDF");
    }
    // Reset downloading state after the browser has had time to initiate the download.
    setTimeout(() => setDownloading(false), 1500);
  };

  // Apre la modale "Invia analisi" e pre-carica il link del portale.
  const openShareDialog = async () => {
    setShareOpen(true);
    setCopied(false);
    if (shareLink) return; // cache per apertura ripetuta
    setShareLoading(true);
    try {
      const res = await api.get(`/analyses/${id}/share-token`);
      const relUrl = res?.data?.url || `/r/${res?.data?.token}`;
      // Costruiamo un link ASSOLUTO (utile per WhatsApp/Email/copia).
      const absUrl = `${window.location.origin}${relUrl}`;
      setShareLink(absUrl);
    } catch {
      toast.error("Impossibile generare il link. Riprova.");
      setShareOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const whatsappMessage = shareLink
    ? "Ciao,\n"
      + "Le invio la Sua analisi tricologica personalizzata.\n\n"
      + "Può consultarla comodamente da questo link:\n"
      + `${shareLink}\n\n`
      + "Per qualsiasi chiarimento resto a Sua disposizione."
    : "";
  // wa.me richiede solo le cifre (no "+", no spazi). Il telefono cliente è
  // salvato in formato E.164 (es. "+393331234567") → strippiamo il "+".
  const clientPhoneDigits = (client?.telefono || "").replace(/[^0-9]/g, "");
  const hasClientPhone = clientPhoneDigits.length >= 6;
  const hasClientEmail = !!(client?.email || "").trim();
  const whatsappHref = shareLink && hasClientPhone
    ? `https://wa.me/${clientPhoneDigits}?text=${encodeURIComponent(whatsappMessage)}`
    : "#";
  const emailHref = shareLink && hasClientEmail
    ? `mailto:${encodeURIComponent(client.email)}`
      + `?subject=${encodeURIComponent("La Sua analisi tricologica personalizzata")}`
      + `&body=${encodeURIComponent(
          "Gentile cliente,\n\n"
          + "Le invio la Sua analisi tricologica personalizzata.\n\n"
          + `Può consultarla comodamente da questo link:\n${shareLink}\n\n`
          + "Per qualsiasi chiarimento resto a Sua disposizione.\n\n"
          + "Cordiali saluti."
        )}`
    : "#";

  // Link "brandizzato" da mostrare nell'UI (più corto, più pulito).
  // Il click usa comunque `shareLink` (URL reale con token JWT).
  const prettyShareLink = shareLink
    ? (() => {
        try {
          const u = new URL(shareLink);
          const token = (u.pathname.split("/r/")[1] || "").slice(0, 6);
          return `${u.host}/r/${token}…`;
        } catch {
          return shareLink;
        }
      })()
    : "";

  // Share nativo del device (iOS AirDrop, Android share sheet, ecc.)
  const canNativeShare = typeof navigator !== "undefined"
    && typeof navigator.share === "function";
  const shareNative = async () => {
    if (!canNativeShare || !shareLink) return;
    try {
      await navigator.share({
        title: "Analisi tricologica",
        text: whatsappMessage,
        url: shareLink,
      });
    } catch {
      // utente ha annullato → silenzio
    }
  };

  const clientFileName = (() => {
    const raw = (client?.name || "Cliente").trim() || "Cliente";
    return `Analisi - ${raw.toUpperCase()}.pdf`;
  })();

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-12 w-64 shimmer rounded-xl" />
          <div className="h-64 shimmer rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (locked) {
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#C5A059] bg-white p-10 text-center animate-fade-up"
          data-testid="analysis-locked-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Analisi bloccata</p>
          <h1 className="font-serif-display text-4xl text-[#1A1A1A] mb-4 leading-tight">
            Questa analisi è già stata<br />visualizzata una volta
          </h1>
          <p className="text-[#737373] max-w-lg mx-auto mb-8 leading-relaxed">
            Nel piano <span className="font-semibold text-[#1A1A1A]">Free</span> ogni analisi è
            consultabile una sola volta. Passa a <span className="font-semibold text-[#1A1A1A]">Basic</span>
            {" "}per sbloccare lo storico completo e il download PDF dei tuoi protocolli.
          </p>
          <Link
            to="/piani"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all"
            data-testid="analysis-locked-upgrade"
          >
            <Crown className="w-4 h-4" />
            Passa a Basic
          </Link>
        </div>
      </Layout>
    );
  }

  // ---- Pipeline async: UI dedicata per processing / failed ----------------
  if (pipelineStatus && pipelineStatus.status === "processing") {
    const stage = pipelineStatus.progress_stage || "queued";
    const stageLabel = {
      queued: "In coda…",
      observation: "Osservazione oggettiva (Fase 1/2)",
      interpretation: "Interpretazione tricologica (Fase 2/2)",
      protocol: "Generazione protocollo Medavita",
      done: "Finalizzazione…",
    }[stage] || "In elaborazione…";
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-from-processing">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#E5E5E0] bg-white p-10 sm:p-12 text-center animate-fade-up"
          data-testid="analysis-processing-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#FAF6EE] to-[#F3E8CB] border border-[#C5A059]/40 flex items-center justify-center mb-6">
            <Loader2 className="w-7 h-7 text-[#C5A059] animate-spin" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Analisi in corso</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mb-3 leading-tight">
            Stiamo elaborando le foto
          </h1>
          <p className="text-[#737373] max-w-md mx-auto mb-6 leading-relaxed">
            L'AI sta analizzando la cute e il fusto per generare il protocollo personalizzato.
            L'operazione richiede in genere 45-75 secondi.
          </p>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAFAFA] border border-[#E5E5E0] text-sm text-[#1A1A1A]"
            data-testid="analysis-processing-stage"
          >
            <span className="w-2 h-2 rounded-full bg-[#C5A059] animate-pulse" />
            {stageLabel}
          </div>
          <p className="text-xs text-[#A3A3A3] mt-8 leading-relaxed max-w-md mx-auto">
            Puoi chiudere questa pagina in qualsiasi momento: l'analisi continuerà in background e la troverai nella scheda del cliente.
          </p>
        </div>
      </Layout>
    );
  }

  // ---- Pipeline async: dialog di conferma conflitti foto ↔ questionario ----
  // INTERVENTO 3 / passo 3b-2: l'analisi può fermarsi in `awaiting_confirmation`
  // se ci sono conflitti ad alto impatto (caduta, diradamento, cute sensibile,
  // rossore) tra quanto dichiarato nel questionario e quanto la foto mostra.
  if (pipelineStatus && pipelineStatus.status === "awaiting_confirmation") {
    const conflicts = pipelineStatus.pending_confirmations || [];
    return (
      <Layout>
        <Link
          to={`/client/${pipelineStatus.client_id || ""}`}
          className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group"
          data-testid="back-from-awaiting"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna al cliente</span>
        </Link>
        <ConflictConfirmDialog
          analysisId={id}
          conflicts={conflicts}
          onResolved={async (statusData) => {
            // La POST resolve risponde già con status=completed.
            setPipelineStatus(statusData);
            await loadFullAnalysis();
            toast.success("Protocollo aggiornato");
          }}
        />
      </Layout>
    );
  }

  if (pipelineStatus && pipelineStatus.status === "failed") {
    const errMsg = pipelineStatus.error_message
      || "L'analisi non è stata completata. Riprova tra qualche istante.";
    const errCode = pipelineStatus.error_code || "";
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-from-failed">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#F4CDCC] bg-white p-10 sm:p-12 text-center animate-fade-up"
          data-testid="analysis-failed-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FDF2F2] border border-[#F4CDCC] flex items-center justify-center mb-6">
            <AlertCircle className="w-7 h-7 text-[#D9534F]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#D9534F] mb-3">Analisi non completata</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mb-3 leading-tight">
            Qualcosa è andato storto
          </h1>
          <p className="text-[#737373] max-w-md mx-auto mb-8 leading-relaxed">
            {errMsg}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to={`/client/${pipelineStatus.client_id}/analysis`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all"
              data-testid="analysis-failed-retry"
            >
              <RefreshCw className="w-4 h-4" />
              Riprova l'analisi
            </Link>
            <Link
              to={`/client/${pipelineStatus.client_id}`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-sm font-medium transition-all"
            >
              Torna al cliente
            </Link>
          </div>
          {errCode && (
            <p className="text-[10px] text-[#A3A3A3] mt-6 tracking-wide uppercase">
              Codice errore: {errCode}
            </p>
          )}
        </div>
      </Layout>
    );
  }

  if (!analysis) return null;

  const severityStyles = {
    severo: "bg-red-50 text-red-700 border-red-200",
    moderato: "bg-amber-50 text-amber-700 border-amber-200",
    lieve: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  return (
    <Layout>
      <Link to={`/client/${analysis.client_id}`} className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-to-client-detail">
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-sm">Torna alla scheda cliente</span>
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Protocollo tricologico</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
            {client?.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-[#737373]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(analysis.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            {analysis.livello_gravita && (
              <span className={`text-[10px] tracking-wider uppercase px-3 py-1 rounded-full font-medium border ${severityStyles[analysis.livello_gravita] || ""}`}>
                Intensità: {analysis.livello_gravita}
              </span>
            )}
          </div>
        </div>
        {(() => {
          const hasGuide =
            analysis.guida_consulenza &&
            typeof analysis.guida_consulenza === "object" &&
            Object.keys(analysis.guida_consulenza).length > 0;
          return (
            <div className="flex flex-wrap items-center gap-3" data-testid="analysis-actions">
              {hasGuide && (
                <button
                  onClick={() => setGuideOpen(true)}
                  className="h-12 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white font-medium flex items-center gap-2 transition-all border border-[#C5A059]/30"
                  data-testid="open-consultation-guide"
                >
                  <Briefcase className="w-4 h-4 text-[#C5A059]" />
                  Guida consulenza
                </button>
              )}
              {canDownloadPdf ? (
                <button
                  onClick={openShareDialog}
                  className="h-12 px-6 rounded-xl bg-[#C5A059] hover:bg-[#B38F4E] text-white font-medium flex items-center gap-2 transition-all"
                  data-testid="open-share-dialog"
                >
                  <Send className="w-4 h-4" />
                  Invia analisi
                </button>
              ) : (
                <Link
                  to="/piani"
                  className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium flex items-center gap-2 transition-all"
                  data-testid="pdf-upgrade-cta"
                >
                  <Crown className="w-4 h-4" />
                  Sblocca invio con Basic
                </Link>
              )}
            </div>
          );
        })()}
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Photos */}
        {analysis.photos?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-6 animate-fade-up">
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">Fotografie analizzate</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {analysis.photos.map((p, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-[#FAFAFA]">
                  <div className="aspect-square overflow-hidden">
                    <img src={`data:image/jpeg;base64,${p.data}`} alt={p.label} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-center py-2 text-[#737373]">{p.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-up comparison block — shown in the lead slot when
            tipo_analisi == "followup". Aggiuntivo: non sostituisce mai
            la diagnosi corrente, solo la contestualizza rispetto alla baseline. */}
        {analysis.tipo_analisi === "followup" && (
          <div
            className="col-span-12 rounded-2xl border border-[#C5A059]/40 bg-[#FAF6EE]/70 p-8 animate-fade-up"
            data-testid="followup-section"
          >
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-white border border-[#C5A059]/40 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#8A6B2E]" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[#8A6B2E] font-medium">Follow-up</p>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-serif-display text-2xl text-[#1A1A1A]">
                    Evoluzione rispetto alla baseline
                  </p>
                  {analysis.followup_anticipato && (
                    <span
                      className="inline-flex items-center text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid="followup-anticipato-badge"
                      title="Questo follow-up è stato eseguito prima dei 30 giorni consigliati"
                    >
                      Anticipato
                    </span>
                  )}
                </div>
              </div>
              {analysis.previous_analysis_id && (
                <Link
                  to={`/analysis/${analysis.previous_analysis_id}`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] transition-colors"
                  data-testid="followup-baseline-link"
                >
                  <GitCompare className="w-3.5 h-3.5" />
                  Apri analisi precedente
                </Link>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {analysis.confronto && (
                <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-confronto">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
                    Confronto oggettivo
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-relaxed">{analysis.confronto}</p>
                </div>
              )}
              {analysis.valutazione_risultati && (
                <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-valutazione">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
                    Valutazione risultati
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-relaxed">{analysis.valutazione_risultati}</p>
                </div>
              )}
              {analysis.spiegazione_cliente_followup && (
                <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-spiegazione-cliente">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
                    Spiegazione per la cliente
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-relaxed italic">
                    {analysis.spiegazione_cliente_followup}
                  </p>
                </div>
              )}
              {analysis.guida_operatore_followup && (
                <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-guida-operatore">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
                    Guida operatore (solo salone)
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-relaxed">
                    {analysis.guida_operatore_followup}
                  </p>
                </div>
              )}
              {analysis.protocollo_aggiornato && (
                <div
                  className="md:col-span-2 rounded-xl bg-white border border-[#E5E5E0] p-5"
                  data-testid="followup-protocollo"
                >
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
                    Protocollo aggiornato
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-relaxed">
                    {analysis.protocollo_aggiornato}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Objective observation */}
        {analysis.osservazione_oggettiva && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-8 animate-fade-up">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#737373]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#737373] font-medium">Fase 1</p>
                <p className="font-serif-display text-2xl text-[#1A1A1A]">Osservazione oggettiva</p>
              </div>
            </div>
            <p className="text-xs text-[#737373] italic mb-4">
              Descrizione dei soli rilievi visibili nelle fotografie, senza interpretazione.
            </p>
            <p className="text-[#1A1A1A] leading-relaxed text-[15px]" data-testid="observation-text">
              {analysis.osservazione_oggettiva}
            </p>
          </div>
        )}

        {/* Plain-language client explanation (additive, never replaces technical sections) */}
        {analysis.spiegazione_semplice_cliente && (
          <div
            className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-[#FAF6EE]/60 p-8 animate-fade-up"
            data-testid="client-explanation-section"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white border border-[#E5E5E0] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#C5A059]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Per il cliente</p>
                <p className="font-serif-display text-2xl text-[#1A1A1A]">Spiegazione semplice</p>
              </div>
            </div>
            <p
              className="text-[#1A1A1A] leading-relaxed text-[15px] italic"
              data-testid="client-explanation-text"
            >
              {analysis.spiegazione_semplice_cliente}
            </p>
          </div>
        )}

        {/* Consultation guide is now on-demand — see the "Guida consulenza" button
            in the top action bar. It opens a dedicated modal. */}

        {/* Interpretation */}
        <div className={`col-span-12 rounded-2xl border border-[#E5E5E0] bg-gradient-to-br from-white to-[#FAFAFA] p-8 animate-fade-up`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#EAE5DF] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Fase 2</p>
              <p className="font-serif-display text-2xl text-[#1A1A1A]">Interpretazione tricologica</p>
            </div>
          </div>
          <p className="text-[#1A1A1A] leading-relaxed text-[15px]" data-testid="diagnosis-text">
            {analysis.interpretazione_tricologica || analysis.diagnosi}
          </p>
          {analysis.condizioni_rilevate?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
              <p className="text-xs uppercase tracking-wider text-[#737373] font-medium mb-3">Condizioni rilevate</p>
              <div className="flex flex-wrap gap-2">
                {analysis.condizioni_rilevate.map((c, i) => (
                  <span key={i} className="text-sm px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1A1A1A]">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Nota Professionale (additiva — collassabile, parte chiusa) */}
        <div className="col-span-12 animate-fade-up">
          <ProfessionalNote />
        </div>

        {/* Goals */}
        {analysis.obiettivi?.length > 0 && (
          <div className="col-span-12 lg:col-span-5 rounded-2xl border border-[#E5E5E0] bg-white p-6 animate-fade-up">
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">Obiettivi del trattamento</p>
            <ul className="space-y-3">
              {analysis.obiettivi.map((g, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
                  <span className="text-[#1A1A1A] text-sm leading-relaxed">{g}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Home routine — real Medavita products with priority + usage steps */}
        {/* INTERVENTO 3 / passo 3c — Dichiarazioni della cliente
            (questionario pre-analisi). Renderizzato solo se almeno una
            risposta è presente. Inserito subito prima della routine
            domiciliare. */}
        <QuestionnaireDeclarations
          questionnaire={analysis.cute_questionnaire}
          conflictResolution={analysis.conflict_resolution}
        />

        {analysis.routine_domiciliare?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-8 animate-fade-up">
            <div className="flex items-center gap-2 mb-5">
              <Home className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Routine domiciliare</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {analysis.routine_domiciliare.map((r, i) => {
                const pr = r.priority_level || "";
                const sched = r.scheduling_label || "";
                const badgeLabel = pr === "primary" ? "Primario"
                  : pr === "secondary" ? "Secondario"
                  : pr === "supportive" ? "Opzionale"
                  : "";
                const badgeCls = pr === "primary" ? "bg-[#1A1A1A] text-white"
                  : pr === "secondary" ? "bg-[#C5A059] text-white"
                  : "bg-white text-[#737373] border border-[#E5E5E0]";
                const schedCls = r.scheduling_mode === "alternanza"
                  ? "bg-[#FFF5E5] text-[#C87D2A] border border-[#F2D9A7]"
                  : r.scheduling_mode === "fase_successiva"
                  ? "bg-[#FAFAFA] text-[#737373] border border-dashed border-[#C5A059]"
                  : r.scheduling_mode === "fase_attiva"
                  ? "bg-[#E8F2EC] text-[#1E6B47] border border-[#C3DECE]"
                  : "";
                return (
                  <div
                    key={i}
                    className="p-5 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] bg-[#FAFAFA] transition-colors"
                    data-testid={`home-routine-item-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          {badgeLabel && (
                            <span className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold ${badgeCls}`}>
                              {badgeLabel}
                            </span>
                          )}
                          {sched && (
                            <span
                              className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold ${schedCls}`}
                              data-testid={`scheduling-${r.scheduling_mode}`}
                            >
                              {sched}
                            </span>
                          )}
                        </div>
                        <p className="font-serif-display text-lg text-[#1A1A1A] leading-tight">{r.nome_prodotto}</p>
                        {r.linea && (
                          <p className="text-[10px] uppercase tracking-wider text-[#737373] mt-1">
                            Linea {r.linea}
                          </p>
                        )}
                      </div>
                      {r.categoria && (
                        <span className="shrink-0 text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#737373] font-medium">
                          {r.categoria}
                        </span>
                      )}
                    </div>
                    {r.funzione && (
                      <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2 mt-3">
                        Funzione: {r.funzione}
                      </p>
                    )}
                    {r.scheduling_note && (
                      <p className="text-xs text-[#737373] italic mb-2 border-l-2 border-[#C5A059] pl-3 leading-relaxed">
                        {r.scheduling_note}
                      </p>
                    )}
                    <div className="border-t border-[#E5E5E0] pt-3 mt-3 space-y-1">
                      {r.frequenza && (
                        <p className="text-xs text-[#737373]">
                          <span className="font-medium text-[#1A1A1A]">Frequenza:</span> {r.frequenza}
                        </p>
                      )}
                      {(r.codice_prodotto || r.formato) && (
                        <p className="text-xs text-[#737373]">
                          {r.codice_prodotto && <span><span className="font-medium text-[#1A1A1A]">Cod.</span> {r.codice_prodotto}</span>}
                          {r.codice_prodotto && r.formato && " • "}
                          {r.formato && <span>{r.formato}</span>}
                        </p>
                      )}
                      {r.prezzo_eur != null && (
                        <p className="text-xs" data-testid={`home-price-${i}`}>
                          <span className="font-medium text-[#1A1A1A]">Prezzo:</span>{" "}
                          <span className="text-[#C5A059] font-semibold">{formatEuro(r.prezzo_eur)}</span>
                        </p>
                      )}
                      <label
                        className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none"
                        data-testid={`sold-home-${i}`}
                      >
                        <input
                          type="checkbox"
                          checked={sold.has(r.nome_prodotto)}
                          onChange={() => toggleSold(r.nome_prodotto)}
                          className="w-4 h-4 accent-[#C5A059] cursor-pointer"
                        />
                        <span className={`text-xs font-medium ${sold.has(r.nome_prodotto) ? "text-[#C5A059]" : "text-[#737373]"}`}>
                          {sold.has(r.nome_prodotto) ? "Venduto ✓" : "Venduto"}
                        </span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Usage instructions */}
            <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">
                Modalità di utilizzo a casa
              </p>
              <div className="space-y-4">
                {analysis.routine_domiciliare.map((r, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0]" data-testid={`usage-steps-${i}`}>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-3">
                      {r.nome_prodotto}
                      {r.categoria && <span className="text-xs text-[#737373] ml-2">({r.categoria})</span>}
                    </p>
                    <ol className="space-y-3 ml-1">
                      {(r.modalita_utilizzo || []).map((s, j) => {
                        const step = typeof s === "string" ? s : s?.step;
                        const beneficio = typeof s === "string" ? "" : s?.beneficio;
                        return (
                          <li key={j} className="text-sm flex gap-2.5">
                            <span className="text-[#C5A059] font-semibold shrink-0">{j + 1}.</span>
                            <div className="flex-1">
                              <p className="text-[#1A1A1A]">{step}</p>
                              {beneficio && (
                                <p className="text-xs text-[#737373] italic mt-0.5 flex gap-1.5">
                                  <span className="text-[#C5A059]">→</span>
                                  <span>{beneficio}</span>
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Salon protocol — real operational procedure */}
        {analysis.protocollo_salone && (analysis.protocollo_salone.prodotti?.length > 0 || analysis.protocollo_salone.procedura_operativa?.length > 0) && (
          <div className="col-span-12 rounded-2xl border border-[#1A1A1A] bg-[#1A1A1A] text-white p-8 animate-fade-up">
            <div className="flex items-center gap-2 mb-6">
              <Scissors className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Protocollo Salone</p>
              <span className="ml-auto text-sm text-white/70">
                {analysis.protocollo_salone.frequenza || "1 volta al mese"}
              </span>
            </div>

            {/* Products used */}
            {analysis.protocollo_salone.prodotti?.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]/80 mb-3 font-medium">
                  Prodotti utilizzati
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {analysis.protocollo_salone.prodotti.map((p, i) => {
                    const pr = p.priority_level || "";
                    const badgeLabel = pr === "primary" ? "Primario"
                      : pr === "secondary" ? "Secondario"
                      : pr === "supportive" ? "Opzionale"
                      : "";
                    const badgeCls = pr === "primary" ? "bg-white text-[#1A1A1A]"
                      : pr === "secondary" ? "bg-[#C5A059] text-white"
                      : "bg-white/10 text-white/80 border border-white/20";
                    return (
                      <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-[#C5A059]/50 transition-colors" data-testid={`salon-product-${i}`}>
                        {badgeLabel && (
                          <span className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold mb-2 ${badgeCls}`}>
                            {badgeLabel}
                          </span>
                        )}
                        <p className="font-serif-display text-lg text-white leading-tight">{p.nome_prodotto}</p>
                        <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
                          Linea {p.linea} · {p.categoria}
                        </p>
                        {p.funzione && (
                          <p className="text-xs text-[#C5A059] italic mt-3">{p.funzione}</p>
                        )}
                        {(p.codice_prodotto || p.formato) && (
                          <p className="text-xs text-white/50 mt-2">
                            {p.codice_prodotto && <span>Cod. {p.codice_prodotto}</span>}
                            {p.codice_prodotto && p.formato && " • "}
                            {p.formato && <span>{p.formato}</span>}
                          </p>
                        )}
                        <p className="text-[10px] text-white/40 italic mt-3 uppercase tracking-wider">
                          Trattamento professionale — non in vendita
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Operational procedure */}
            {analysis.protocollo_salone.procedura_operativa?.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]/80 mb-3 font-medium">
                  Procedura operativa
                </p>
                <ol className="space-y-2">
                  {analysis.protocollo_salone.procedura_operativa.map((step, i) => (
                    <li key={i} className="text-sm text-white/90 leading-relaxed pl-4 border-l-2 border-[#C5A059]/40" data-testid={`procedure-step-${i}`}>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Objective */}
            {analysis.protocollo_salone.obiettivo_trattamento && (
              <div className="pt-6 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2 font-medium">
                  Obiettivo del trattamento in salone
                </p>
                <p className="text-sm text-white/90 leading-relaxed" data-testid="salon-objective">
                  {analysis.protocollo_salone.obiettivo_trattamento}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Riepilogo prodotti consigliati — shopping list with total (home routine ONLY) */}
        {analysis.riepilogo_prodotti?.prodotti?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#C5A059] bg-white p-8 animate-fade-up" data-testid="riepilogo-section">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">
                Riepilogo Prodotti Consigliati
              </p>
            </div>
            <p className="text-xs text-[#737373] italic mb-5">
              Prodotti da acquistare per la routine domiciliare
            </p>

            <div className="divide-y divide-[#E5E5E0]">
              {analysis.riepilogo_prodotti.prodotti.map((p, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-3 items-start py-3"
                  data-testid={`riepilogo-item-${i}`}
                >
                  <div className="col-span-12 md:col-span-8">
                    <p className="text-sm font-medium text-[#1A1A1A] leading-snug">
                      {p.nome_prodotto}
                    </p>
                  </div>
                  <div className="col-span-6 md:col-span-2 text-sm text-[#737373]">
                    {p.formato || "—"}
                  </div>
                  <div
                    className="col-span-6 md:col-span-2 text-sm text-[#1A1A1A] font-semibold md:text-right"
                    data-testid={`riepilogo-price-${i}`}
                  >
                    {formatEuro(p.prezzo_eur)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t-2 border-[#1A1A1A] flex items-baseline justify-between">
              <p className="text-sm uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold">
                Totale prodotti
              </p>
              <p
                className="text-2xl font-serif-display text-[#C5A059] font-semibold"
                data-testid="riepilogo-total"
              >
                {formatEuro(analysis.riepilogo_prodotti.totale_eur || 0)}
              </p>
            </div>

            <p className="mt-3 text-[11px] text-[#737373] italic leading-relaxed">
              I prodotti del protocollo salone sono professionali e non sono
              inclusi in questo riepilogo.
            </p>
          </div>
        )}

        {/* Extra notes */}
        {analysis.note_aggiuntive && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-[#FAFAFA] p-6 animate-fade-up">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-1">Note aggiuntive</p>
                <p className="text-sm text-[#1A1A1A] leading-relaxed">{analysis.note_aggiuntive}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Consultation guide — on-demand modal, dark/gold theme, never in the PDF */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent
          className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 bg-[#1A1A1A] text-white border border-[#C5A059]/30 rounded-2xl"
          data-testid="consultation-guide-modal"
        >
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-white/10">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <DialogTitle className="font-serif-display text-2xl text-white leading-tight">
                    Guida alla consulenza · Solo per te
                  </DialogTitle>
                  <DialogDescription className="text-xs text-white/60 mt-1">
                    Strumento riservato al professionista
                  </DialogDescription>
                </div>
              </div>
              <span className="hidden sm:inline-flex text-[10px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/40 px-2.5 py-1 rounded-full bg-black/40 shrink-0 mt-1">
                Riservato
              </span>
            </div>
          </DialogHeader>

          <div className="px-8 py-6 grid md:grid-cols-2 gap-5">
            {analysis?.guida_consulenza?.cosa_sta_succedendo?.length > 0 && (
              <div
                className="rounded-xl bg-white/5 border border-white/10 p-5"
                data-testid="guide-cosa-sta-succedendo"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] font-medium mb-3">
                  Cosa sta succedendo
                </p>
                <ul className="space-y-2">
                  {analysis.guida_consulenza.cosa_sta_succedendo.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-white/90">
                      <span className="text-[#C5A059] mt-1">›</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CANTIERE STABILITÀ AI / passo 1 — Parametri visivi pass 1.
                Mostrati SOLO al parrucchiere (siamo dentro "Solo per te").
                NON nel PDF cliente: il backend strippa il blocco dalla
                osservazione esposta, qui leggiamo il campo strutturato
                `parametri_foto` indipendente. */}
            {(() => {
              const pf = analysis?.parametri_foto;
              if (!pf || typeof pf !== "object") return null;
              const items = [
                { k: "sebo",          label: "Sebo" },
                { k: "osti",          label: "Osti follicolari" },
                { k: "desquamazione", label: "Desquamazione" },
                { k: "rossore",       label: "Rossore" },
                { k: "cute",          label: "Cute" },
              ].filter((it) => (pf[it.k] || "").length > 0);
              if (items.length === 0) return null;
              const _pretty = (v) => (v || "").replace(/_/g, " ");
              return (
                <div
                  className="rounded-xl bg-white/5 border border-white/10 p-5 md:col-span-2"
                  data-testid="guide-parametri-foto"
                >
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] font-medium mb-3">
                    Parametri visivi rilevati
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {items.map((it) => (
                      <li
                        key={it.k}
                        className="flex justify-between gap-3 text-sm text-white/90 border-b border-white/5 py-1.5"
                        data-testid={`param-${it.k}`}
                      >
                        <span className="text-white/60">{it.label}</span>
                        <span className="text-white font-medium">{_pretty(pf[it.k])}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {analysis?.guida_consulenza?.come_spiegarlo && (
              <div
                className="rounded-xl bg-white/5 border border-white/10 p-5"
                data-testid="guide-come-spiegarlo"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] font-medium mb-3">
                  Come spiegarlo al cliente
                </p>
                <p className="text-sm leading-relaxed italic text-white/90">
                  &ldquo;{analysis.guida_consulenza.come_spiegarlo}&rdquo;
                </p>
              </div>
            )}

            {analysis?.guida_consulenza?.cosa_proporre && (
              <div
                className="rounded-xl bg-white/5 border border-white/10 p-5 md:col-span-2"
                data-testid="guide-cosa-proporre"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] font-medium mb-3">
                  Cosa proporre
                </p>
                <p className="text-sm leading-relaxed text-white/90">
                  {analysis.guida_consulenza.cosa_proporre}
                </p>
              </div>
            )}

            {analysis?.guida_consulenza?.errori_da_evitare?.length > 0 && (
              <div
                className="rounded-xl bg-[#3B1A1A]/40 border border-rose-400/20 p-5 md:col-span-2"
                data-testid="guide-errori-da-evitare"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-rose-300 font-medium mb-3 flex items-center gap-2">
                  <XCircle className="w-3.5 h-3.5" />
                  Errori da evitare
                </p>
                <ul className="space-y-2">
                  {analysis.guida_consulenza.errori_da_evitare.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-white/85">
                      <span className="text-rose-300 mt-1">×</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CANTIERE STABILITÀ AI / passo 2 — Avvisi sulle discrepanze
                tra parametri foto (pass 1 strutturato) e tag del
                questionario. Calcolati dal backend in
                `discrepanze_foto_questionario`. Stile arancione, stesso
                pattern di D7/D9. SOLO professionale, MAI nel PDF. */}
            {Array.isArray(analysis?.discrepanze_foto_questionario) &&
              analysis.discrepanze_foto_questionario.length > 0 && (
                <div
                  className="rounded-xl bg-[#3B2A1A]/40 border border-orange-300/25 p-5 md:col-span-2"
                  data-testid="guide-discrepanze-foto-questionario"
                >
                  <p className="text-[11px] uppercase tracking-[0.18em] text-orange-300 font-medium mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Attenzione: possibili discrepanze foto/dichiarato
                  </p>
                  <ul className="space-y-3">
                    {analysis.discrepanze_foto_questionario.map((d, i) => (
                      <li
                        key={`${d.regola}-${i}`}
                        className="flex gap-3 text-sm leading-relaxed text-white/90"
                        data-testid={`discrepanza-${d.regola}`}
                      >
                        <span className="text-orange-300 mt-1 shrink-0">!</span>
                        <span>{d.testo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
            )}

            {/* INTERVENTO 3 / passo 3e — Avvisi dal questionario.
                Generati deterministicamente da `cute_questionnaire`. Mostrati
                SOLO al parrucchiere (siamo dentro il dialog Guida consulenza,
                già etichettato come "Solo per te"). NON nel PDF cliente. */}
            {(() => {
              const q = analysis?.cute_questionnaire || {};
              const warnings = [];
              // D7: caduta dichiarata + durata > 3 mesi
              if (
                ["leggera", "moderata", "importante"].includes(q.caduta) &&
                q.caduta_durata === ">3m"
              ) {
                warnings.push({
                  id: "d7-caduta-cronica",
                  text: "La cliente riferisce caduta da più di 3 mesi. Valuta se suggerire una verifica dermatologica o tricologica specialistica prima di procedere solo con approccio cosmetico.",
                });
              }
              // D9: utilizza già prodotti specifici per la cute
              if (q.prodotti_cute_in_uso === "si") {
                warnings.push({
                  id: "d9-prodotti-in-uso",
                  text: "La cliente sta già utilizzando prodotti specifici per la cute. Verifica la routine in corso prima di proporre nuovi prodotti: potrebbero esserci sovrapposizioni o incompatibilità.",
                });
              }
              if (warnings.length === 0) return null;
              return (
                <div
                  className="rounded-xl bg-[#1A2A3B]/40 border border-amber-300/25 p-5 md:col-span-2"
                  data-testid="guide-questionnaire-warnings"
                >
                  <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300 font-medium mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Avvisi dal questionario
                  </p>
                  <ul className="space-y-3">
                    {warnings.map((w) => (
                      <li
                        key={w.id}
                        className="flex gap-3 text-sm leading-relaxed text-white/90"
                        data-testid={`guide-warning-${w.id}`}
                      >
                        <span className="text-amber-300 mt-1 shrink-0">!</span>
                        <span>{w.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          <div className="px-8 py-5 border-t border-white/10 bg-black/20">
            <p className="text-xs text-white/60 leading-relaxed text-center">
              Questa guida è visibile solo al professionista e non viene inclusa nel report cliente.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog "Condividi analisi" — layout in stile Bridal Manager */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-[640px] p-0 overflow-hidden" data-testid="share-dialog">
          <div className="px-7 sm:px-8 pt-7 pb-5">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-[28px] sm:text-3xl text-[#1A1A1A] leading-tight">
                Condividi analisi
              </DialogTitle>
              <DialogDescription className="text-[15px] text-[#737373] leading-relaxed mt-2">
                Il cliente riceverà un link interattivo con il protocollo personalizzato.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-7 sm:px-8 pb-8 space-y-5">
            {shareLoading || !shareLink ? (
              <div className="h-28 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
              </div>
            ) : (
              <>
                {/* Box file analisi (stile Bridal) */}
                <div
                  data-testid="share-file-box"
                  className="flex items-center gap-4 rounded-xl border border-[#E8E0CE] bg-[#FDFBF4] px-5 py-4"
                >
                  <div className="w-10 h-10 rounded-lg border border-[#E8E0CE] bg-white flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-[#1A1A1A] truncate">
                      {clientFileName}
                    </p>
                    <p className="text-xs text-[#737373] mt-0.5">Pronto</p>
                  </div>
                </div>

                {/* Griglia azioni 2x2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ShareTile
                    testid="share-copy-btn"
                    icon={copied ? Check : LinkIcon}
                    iconClass={copied ? "text-[#16A34A]" : "text-[#C5A059]"}
                    title={copied ? "Link copiato" : "Copia link cliente"}
                    subtitle={copied ? "Appunti aggiornati" : prettyShareLink}
                    onClick={copyShareLink}
                  />
                  <ShareTile
                    testid="share-email-btn"
                    icon={Mail}
                    iconClass="text-[#C5A059]"
                    title={hasClientEmail ? "Invia via Email" : "Email cliente mancante"}
                    subtitle={hasClientEmail ? client.email : "Aggiungi l'email al profilo cliente"}
                    href={hasClientEmail ? emailHref : undefined}
                    onClick={hasClientEmail ? undefined : () => toast.info("Aggiungi un indirizzo email nel profilo cliente per usare questa azione.")}
                    disabled={!hasClientEmail}
                  />
                  <ShareTile
                    testid="share-whatsapp-btn"
                    icon={MessageCircle}
                    iconClass={hasClientPhone ? "text-[#25D366]" : "text-[#A3A3A3]"}
                    title={hasClientPhone ? "Invia via WhatsApp" : "Telefono cliente mancante"}
                    subtitle={
                      hasClientPhone
                        ? `Invia a ${client.telefono}`
                        : "Aggiungi il telefono al profilo cliente"
                    }
                    href={hasClientPhone ? whatsappHref : undefined}
                    external={hasClientPhone}
                    onClick={hasClientPhone ? undefined : () => toast.info("Aggiungi un numero di telefono nel profilo cliente per usare questa azione.")}
                    disabled={!hasClientPhone}
                  />
                  <ShareTile
                    testid="share-download-pdf-btn"
                    icon={Download}
                    iconClass="text-[#C5A059]"
                    title="Scarica PDF"
                    subtitle="Salva il documento sul dispositivo"
                    onClick={downloadPdf}
                    disabled={downloading}
                  />
                  {canNativeShare && (
                    <ShareTile
                      testid="share-native-btn"
                      icon={Share2}
                      iconClass="text-[#C5A059]"
                      title="Condividi…"
                      subtitle="Menu nativo (AirDrop, Mail, …)"
                      onClick={shareNative}
                    />
                  )}
                </div>

                <p className="text-[12px] text-[#A3A3A3] leading-relaxed pt-2 border-t border-[#F2EEE7]">
                  Il link apre una pagina interattiva con il protocollo completo
                  e i contatti del Suo salone. Scade dopo 24 ore.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ShareTile({ icon: Icon, iconClass = "text-[#C5A059]", title, subtitle, onClick, href, external, disabled, testid }) {
  const Component = href ? "a" : "button";
  const extraProps = href
    ? { href, ...(external ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
    : { type: "button", onClick, disabled };
  return (
    <Component
      {...extraProps}
      data-testid={testid}
      className="group flex items-start gap-4 rounded-xl border border-[#E5E5E0] bg-white hover:border-[#C5A059] hover:bg-[#FDFBF4] transition-all px-4 py-4 text-left disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="w-10 h-10 rounded-lg bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center shrink-0 group-hover:bg-white">
        <Icon className={`w-5 h-5 ${iconClass}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-medium text-[#1A1A1A] leading-snug truncate">
          {title}
        </p>
        <p className="text-xs text-[#737373] mt-0.5 truncate">{subtitle}</p>
      </div>
    </Component>
  );
}

// Nota Professionale — additiva. Collassata di default ad ogni apertura
// della pagina. Nessun localStorage (richiesta esplicita).
function ProfessionalNote() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F0] transition-all duration-200"
      data-testid="professional-note"
    >
      {!expanded ? (
        <div className="flex items-center gap-3 px-5 py-3">
          <Info className="w-3.5 h-3.5 text-[#8A6B2E] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold leading-tight">
              Nota Professionale
            </p>
            <p className="text-[12px] text-[#5B4A24] leading-snug truncate sm:whitespace-normal mt-0.5">
              AI TricoProtocol™ è uno strumento di supporto cosmetico-tricologico professionale.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 text-[11px] text-[#8A6B2E] hover:text-[#5B4A24] font-medium shrink-0 transition-colors"
            data-testid="professional-note-toggle"
          >
            Approfondisci <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="px-5 py-4" data-testid="professional-note-expanded">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-[#8A6B2E]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold">
                Nota Professionale
              </p>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1 text-[11px] text-[#8A6B2E] hover:text-[#5B4A24] font-medium transition-colors"
              data-testid="professional-note-toggle"
            >
              Comprimi <ChevronUp className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2 text-[12.5px] text-[#5B4A24] leading-relaxed">
            <p>
              AI TricoProtocol™ è uno strumento di supporto all&apos;analisi
              cosmetico-tricologica professionale.
            </p>
            <p>
              Le informazioni riportate hanno finalità cosmetiche e
              professionali e non sostituiscono una valutazione specialistica
              quando necessaria.
            </p>
            <p>
              In presenza di anomalie o condizioni che richiedano ulteriori
              approfondimenti, si raccomanda di indirizzare il cliente a uno
              specialista qualificato.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

